import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Icons from 'components/icons';
import PopperOverlay from 'components/common/popper-overlay';
import ControlButton from 'components/common/control-button';
import Box from '@material-ui/core/Box';
import Portal from '@material-ui/core/Portal';
import BrightnessPanel from './brightness-panel';
import useStyles from './settings-menu.styles';

const SettingsMenu = memo((props) => {
  const { controlBtnProps, isEbookReaderOpen } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const settingsRef = useRef(null);
  const settingsMenuRef = useRef(null);
  const [brightness, setBrightness] = useState('100');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen((currentFlag) => !currentFlag);
  }, []);

  const closeSettingsMenu = useCallback((event) => {
    if (settingsRef.current && settingsRef.current.contains(event?.target)) {
      return;
    }

    setIsSettingsOpen(false);
  }, []);

  const handleSettingsMenuKeyDown = useCallback((event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.stopPropagation();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setIsSettingsOpen(false);
      settingsRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (!isSettingsOpen) {
      return;
    }
    settingsMenuRef.current
      ?.querySelector('[tabindex]:not([tabindex="-1"]):not([disabled])')
      ?.focus();
  }, [isSettingsOpen]);

  return (
    <>
      <ControlButton
        buttonRef={settingsRef}
        onClick={toggleSettings}
        icon={Icons.ReaderSettings}
        {...controlBtnProps}
        aria-label={t('reader_icon_text_setting')}
        title={t('reader_icon_text_setting')}
      />
      <PopperOverlay
        isOpen={isSettingsOpen}
        placement="bottom"
        onClose={closeSettingsMenu}
        anchor={settingsRef.current}
        popperClassName={classes.paper}
      >
        <Box ref={settingsMenuRef} onKeyDown={handleSettingsMenuKeyDown}>
          <BrightnessPanel brightness={brightness} setBrightness={setBrightness} />
        </Box>
      </PopperOverlay>
      {isEbookReaderOpen && (
        <Portal container={document.getElementById('root')}>
          <Box
            className={classes.brightnessOverlay}
            style={{ opacity: 1 - parseInt(`${brightness ?? '100'}`, 10) / 100 }}
          />
        </Portal>
      )}
    </>
  );
});

SettingsMenu.propTypes = {
  controlBtnProps: PropTypes.shape({
    className: PropTypes.string,
    iconClassName: PropTypes.string,
  }),
  isEbookReaderOpen: PropTypes.bool.isRequired,
};

export default SettingsMenu;
